import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { later } from '@ember/runloop';

import tokenManager from 'mewe/shared/token-manager';
import PublicPagesApi from 'mewe/api/public-pages-api-unauth';
import AuthApi from 'mewe/api/auth-api';
import AccountApi from 'mewe/api/account-api';
import CurrentUserStore from 'mewe/stores/current-user-store';
import { isInJail, isLocked } from 'mewe/utils/jail-utils';

import { startFtue } from 'mewe/utils/dialogs-common';
import { emailRegex, getRedirToInnerAppQueryParam, addSignupUrlParams } from 'mewe/shared/utils';
import { trimAndLower } from 'mewe/utils/miscellaneous-utils';
import { timeFromNow } from 'mewe/utils/datetime-utils.js';
import FunctionalUtils from 'mewe/shared/functional-utils.js';
import { getQueryStringParams, getNextFromUrl } from 'mewe/shared/utils';
import Session from 'mewe/shared/session';
import cookie from 'mewe/shared/cookie';
import config from 'mewe/config';
import i18n from 'i18next';
import jstz from 'jstimezonedetect';
import { AuthProviders } from 'mewe/constants';
import { GoogleAuth } from 'mewe/utils/google-auth';
import { AppleAuth } from 'mewe/utils/apple-auth';
import CodeInputManager from 'mewe/utils/code-input-manager';
import Verbose from 'mewe/utils/verbose';
const verbose = Verbose({ prefix: '[AuthForm]' }).log;

export default class MwHomeAuthFormComponent extends Component {
  challengeOrigin = 'login';
  arkoseKey = config.arkoseKeyLog;
  hcaptchaSiteKey = config.hcaptcha.login;
  hcaptchaElemClass = 'h-captcha_login';

  codeInputManager = new CodeInputManager(6);
  minPasswordLength = 6;

  @service router;
  @service analytics;
  @service dynamicDialogs;
  @service authentication;
  @service account;

  @tracked afterChallengeCallback;
  @tracked challengeReady;

  @tracked email;
  @tracked password;
  @tracked fname;
  @tracked lname;
  @tracked ageConsent;
  @tracked stepNumber; // unknown by default, to be set in constructor
  @tracked isEmailFlow; // on Web show email input by default
  @tracked isRegisterFlow;
  @tracked cannotRegisterWithCC;
  @tracked isDsnpWithoutPassword;

  @tracked invalidEmail = true; // invalid because empty by default
  @tracked invalidPhone = true; // invalid because empty by default
  @tracked invalidPassword = false; // invalidated only by API error response
  @tracked loginBlocked;
  @tracked needConfirmEmail;
  @tracked emailSentText;
  @tracked isIdentifierSubmitted;
  @tracked isPasswordSubmitted;
  @tracked isCheckingIndentity;
  @tracked isAuthRequestInProgress;
  @tracked resendCodeTimeLeft;
  @tracked otpSentAddress;
  @tracked otpPreferred;
  @tracked hideSwitchToCode;
  @tracked isCodeComplete;
  @tracked limitError;
  @tracked error;

  @action
  goToStepZero() {
    this.email = '';
    this.password = '';
    this.fname = '';
    this.lname = '';
    this.ageConsent = false;
    this.isEmailFlow = true; // on Web show email input by default
    this.isRegisterFlow = false;
    this.invalidEmail = true; // invalid because empty by default
    this.invalidPhone = true; // invalid because empty by default
    this.invalidPassword = false; // invalidated only by API error response
    this.loginBlocked = false;
    this.needConfirmEmail = false;
    this.emailSentText = '';
    this.isIdentifierSubmitted = false;
    this.isPasswordSubmitted = false;
    this.isCheckingIndentity = false;
    this.isAuthRequestInProgress = false;
    this.resendCodeTimeLeft = 0;
    this.limitError = false;
    this.error = '';
    this.stepNumber = 0;
    this.codeInputManager.reset(); // Completely reset the code input manager
  }

  constructor() {
    super(...arguments);

    AppleAuth.initialize();

    const blockedUntil = cookie.get('loginBlockedUntil') || 0;
    const isLockedError = +blockedUntil > new Date().getTime();
    if (isLockedError) {
      this.loginLockedUntil = timeFromNow(+blockedUntil, Date.now(), i18n.language);
      this.loginBlocked = true;
      return;
    }

    this.urlParams = getQueryStringParams();

    Session.isAuthenticated().then(({ isAuthenticated }) => {
      this.isLoggedIn = isAuthenticated;

      if (this.isLoggedIn) {
        this.goToApp();
      } else {
        // not logged in user, show identification step
        this.goToStepZero();

        if (~document.location.search.indexOf('next=')) {
          this.error = __(
            `The page you are trying to reach is only available to logged in users. Please login to access.`
          );
        }

        // email can be passed to be prefilled in the form
        if (this.urlParams?.email) {
          this.email = this.urlParams.email;
          this.emailUpdated();
        }
      }
    });
  }

  @action
  emailUpdated() {
    // reset submitted state on any change
    this.isIdentifierSubmitted = false;

    const isValid = emailRegex.test(this.emailValue);
    this.invalidEmail = !isValid;
  }

  @action
  emailKeyDown(e) {
    // prevent adding empty spaces in email input, hack for FF issue (MW-3609)
    if (e.key === ' ') {
      e.preventDefault();
    }
  }

  get emailValue() {
    return trimAndLower(this.email || '');
  }

  get showEmailError() {
    return this.isIdentifierSubmitted && this.invalidEmail;
  }

  @action
  phoneUpdated({ number, code, isValidNumber }) {
    // reset submitted state on any change
    this.isIdentifierSubmitted = false;

    this.phoneCode = `+${code}`;
    this.phoneNumber = number;
    this.invalidPhone = !isValidNumber;
  }

  get showPhoneError() {
    return this.isIdentifierSubmitted && this.invalidPhone;
  }

  get passwordValue() {
    return this.password || '';
  }

  get showPasswordError() {
    return this.isPasswordSubmitted && (this.invalidPassword || this.passwordValue.length < this.minPasswordLength);
  }

  @action
  passwordUpdated() {
    this.invalidPassword = false;
  }

  get issubmitIdentifierDisabled() {
    return (
      !this.challengeReady ||
      this.isCheckingIndentity ||
      (this.isEmailFlow && this.invalidEmail) ||
      (!this.isEmailFlow && this.invalidPhone)
    );
  }

  get isSubmitPasswordDisabled() {
    return this.isAuthRequestInProgress || this.passwordValue.length < this.minPasswordLength;
  }

  get showFooter() {
    return ~[0, 2].indexOf(this.stepNumber);
  }

  get hasErrors() {
    if (this.isEmailFlow) {
      return this.invalidEmail;
    } else {
      return this.invalidPhone;
    }
  }

  get fnameValue() {
    return (this.fname || '').trim();
  }

  get lnameValue() {
    return (this.lname || '').trim();
  }

  get isSubmitNameDisabled() {
    return !this.fnameValue || !this.lnameValue || !this.ageConsent;
  }

  get otpAlreadySent() {
    if (this.otpSentAddress) {
      return this.isEmailFlow ?
            (this.otpSentAddress === this.emailValue) :
            (this.otpSentAddress === `${this.phoneCode}${this.phoneNumber}`);
    }
    return false;
  }

  @action
  startMeweAuth() {
    this.authProvider = AuthProviders.MEWE;
    this.goToIdentifierStep();
  }

  @action
  startGoogleAuth() {
    this.authProvider = AuthProviders.GOOGLE;

    // Pre-open a popup synchronously to avoid popup blockers, then navigate later
    const popupName = GoogleAuth.popupName;
    const popupFeatures = GoogleAuth.getPopupFeatures(500, 600);
    const preOpenedWindow = window.open('about:blank', popupName, popupFeatures);
    // Best-effort sizing/moving before navigation (Safari may ignore)
    if (preOpenedWindow) {
      try {
        preOpenedWindow.resizeTo(500, 600);
        const left = Math.max(0, Math.floor((window.screen.width - 500) / 2));
        const top = Math.max(0, Math.floor((window.screen.height - 600) / 2));
        preOpenedWindow.moveTo(left, top);
      } catch (_) {
        // ignore
      }
    }

    GoogleAuth.hybridSignIn(
      (response) => {
        // this seems to be not used in flow using auth popup,
        // so probably it's for google auth with browser profile, leaving for now
        verbose('Google SIGN-IN - response', response);
        this.idToken = response.id_token;
        this.checkUserByToken();
      },
      (error) => {
        console.error('Google Sign-In error:', error);
      }
    , { preOpenedWindow });

    if (!this.googleAuthListener) {
      // listen for message from popup after auth is completed
      this.googleAuthListener = window.addEventListener('message', (event) => {
        if (event.data?.key === 'authComplete') {
          const response = JSON.parse(event.data.authResponse);
          this.idToken = response.id_token;
          this.checkUserByToken();
        }
      });
    }
  }

  @action
  startAppleAuth() {
    this.authProvider = AuthProviders.APPLE;

    AppleAuth.initializeAppleSignIn(
      (response) => {
        verbose('Apple SIGN-IN - response', response);
        // user is there when signing up with apple for the first time
        if (response.user?.name) {
          this.fname = response.user.name.firstName?.slice(0, 20) || '';
          this.lname = response.user.name.lastName?.slice(0, 20) || '';
        }
        this.idToken = response.authorization.id_token;
        this.checkUserByToken();
      },
      (error) => {
        console.error('Apple Sign-In error:', error);
      }
    );
  }

  checkUserByToken() {
    AuthApi.checkUserByToken(this.authProvider, { idToken: this.idToken })
      .then((response) => {
        this.isRegisterFlow = !response.exists;

        if (this.isRegisterFlow) {
          // name returned only for Google flow, BE decodes it from token.
          // token from Apple doesn't contain name so it's set in previous step if available from Apple response
          if (response.firstName) {
            this.fname = response.firstName?.slice(0, 20) || '';
            this.lname = response.lastName?.slice(0, 20) || '';
          }
          this.goToNameStep();
        } else {
          this.loginWithToken();
        }
      })
      .catch(() => {
        FunctionalUtils.showDefaultErrorMessage();
      });
  }

  @action
  sendCode(callback) {
    // callback is not a function when action comes from template action
    if (typeof callback !== 'function') {
      callback = null;
    }

    const doSend = (challengeToken, provider, onChallengeFailed) => {
      this.startResendCodeTimer();

      const failback = (error) => {
        const resp = error.data;
        if (error.status === 400 && (resp?.errorCode == 117 || resp?.errorCode == 118)) {
          onChallengeFailed?.(); // can happend only for fallback registration where captcha is passed to /register request
        } else if (resp?.limitReached || error.status === 420) {
          this.limitError = true;
        } else {
          FunctionalUtils.showDefaultErrorMessage();
        }
        this.isCheckingIndentity = false;
      };

      let params = {
        session_token: challengeToken,
        challenge_provider: provider,
      };

      if (this.isEmailFlow) {
        params.email = this.emailValue;

        let request = this.isRegisterFlow ? AuthApi.getRegistrationEmailCode(params) : AuthApi.getLoginEmailCode(params);

        request
          .then((data) => {
            this.identifierId = data.identifierId || data.tmpEmailId;
            this.otpSentAddress = params.email;
            callback?.();
          }).catch(failback);

      } else {
        params.countryCode = this.phoneCode;
        params.phone = this.phoneNumber;

        let request = this.isRegisterFlow ? AuthApi.getRegistrationSmsCode(params) : AuthApi.getLoginSmsCode(params);

        request
          .then((data) => {
            this.identifierId = data.identifierId || data.tmpPhoneId;
            this.otpSentAddress = `${params.countryCode}${params.phone}`;
            callback?.();
          })
          .catch(failback);
      }
    };

    // process sending code after completing captcha
    this.afterChallengeCallback = doSend.bind(this);
  }

  startResendCodeTimer() {
    this.resendCodeTimeLeft = this.isEmailFlow ? 180 : 90;

    clearInterval(this.resendCodeInterval);
    this.resendCodeInterval = setInterval(() => {
      if (this.resendCodeTimeLeft > 0) {
        this.resendCodeTimeLeft--;
      } else {
        clearInterval(this.resendCodeInterval);
      }
    }, 1000);
  }

  goToCodeStep() {
    this.codeInputManager.clear();
    this.focusCodeInput();
    this.stepNumber = 3;
  }

  @action
  limitSwitchToPassword() {
    // dismiss limit flag to switch view but set another flag
    // to hide switch to code button
    this.limitError = false;
    this.hideSwitchToCode = true;
    this.goToPasswordStep();
  }

  @action
  goToPasswordStep() {
    this.stepNumber = 4;
    this.isPasswordSubmitted = false;
    this.focusPasswordInput();
  }

  @action
  goToPasswordResetStep() {
    this.stepNumber = 5;
  }

  goToNameStep() {
    this.stepNumber = 2;
    this.focusNameInput();
  }

  goToIdentifierStep() {
    this.stepNumber = 1;
    this.focusEmailInput();
  }

  get canSwitchToCodeStep() {
    // if OTP was preferred then user can't switch to code step because he moved to Password step from there, he should navigate back instead
    return !this.cannotRegisterWithCC && !this.hideSwitchToCode && !this.otpPreferred;
  }

  @action
  switchToCodeStep() {
    // this action is only for Login flow, in Registration user can't switch between OTP/password
    // don't send code for the second time if user navigated from OTP code back and then submit again
    if (this.otpAlreadySent) {
      this.goToCodeStep();
      return;
    }

    // send code only if otpSentAddress check confirms that it wasn't sent already
    this.sendCode(() => {
      this.goToCodeStep();
      this.isCheckingIndentity = false;
    });
  }

  // MEWE auth
  @action
  submitIdentifier(e) {
    e?.preventDefault?.();

    // don't send code for the second time if user navigated back from OTP code step
    if (this.otpAlreadySent) {
      if (this.isRegisterFlow) {
        this.goToNameStep();
      } else {
        if (this.otpPreferred) {
          this.goToCodeStep();
        } else {
          this.goToPasswordStep();
        }
      }
      return;
    } else {
      // identifier has changed and OTP was not sent for this user
      // so also reset the hidden state for switch to code button used when OTP limit is reached
      this.hideSwitchToCode = false;
    }

    // display possible errors
    this.isIdentifierSubmitted = true;

    if (this.hasErrors) {
      return false;
    }

    this.isCheckingIndentity = true;

    let request = this.isEmailFlow ?
      AuthApi.checkAccountByEmail(this.emailValue) :
      AuthApi.checkAccountByPhone(this.phoneCode, this.phoneNumber);

    request
      .then((res) => {
        this.isRegisterFlow = !res.exists;
        this.cannotRegisterWithCC = res.cannotRegisterWithCC; // countries with expensive SMS are forced to register with email
        this.otpPreferred = res.otpPreferred; // last login method stored on BE suggests which method to show as default

        if (this.isRegisterFlow) {
          if (this.cannotRegisterWithCC) {
            this.showNotSupportedCountry();
          } else {
            this.goToNameStep();
          }
          this.isCheckingIndentity = false;
        } else {
          // DSNP users which may not have password - we show additional info message to them in UI
          this.isDsnpWithoutPassword = res.dsnp && res.mayNotHavePass;

          // go to OTP step if BE suggests it, it's based on the last login method
          // and availability of OTP auth for given country (password flow is the default if flag is missing)
          if (this.otpPreferred) {
            this.sendCode(() => {
              this.goToCodeStep();
              this.isCheckingIndentity = false;
            });
          } else {
            this.goToPasswordStep();
            this.isCheckingIndentity = false;
          }
        }
      })
      .catch((err) => {
        const resp = err.data;

        if (resp.message === 'Invalid email') {
          this.invalidEmail = true;
        } else if (resp.errorCode === 115) {
          this.invalidPhone = true;
        } else {
          FunctionalUtils.showDefaultErrorMessage();
        }
        this.isCheckingIndentity = false;
      });
  }

  // MEWE auth
  @action
  submitPassword(e) {
    e?.preventDefault?.();

    this.isPasswordSubmitted = true;

    this.isRegisterFlow ? this.doRegister() : this.loginWithMewe('password');
  }

  // OAuth
  loginWithToken() {
    if (this.isAuthRequestInProgress) return;
    this.isAuthRequestInProgress = true;

    let params = {
      idToken: this.idToken,
    };

    params = addSignupUrlParams(params, this.urlParams);

    // Send to backend for token exchange and user creation/login
    AuthApi.loginWithToken(this.authProvider, params)
      .then((loginResponse) => {
        this.loginCallback(loginResponse);
      })
      .catch((error) => {
        this.loginFailback(error);
      });
  }

  // MEWE auth
  loginWithMewe(method) {
    if (this.isAuthRequestInProgress) return;
    this.isAuthRequestInProgress = true;

    let params = {};

    params = addSignupUrlParams(params, this.urlParams);

    if (method === 'password') {
      const passwordLogin = (challengeToken, provider, onChallengeFailed) => {
        params.password = this.password;
        params.username = this.isEmailFlow ? this.emailValue : `${this.phoneCode}${this.phoneNumber}`;
        params.session_token = challengeToken;
        params.challenge_provider = provider;

        AuthApi.loginWithPassword(params)
          .then((response) => {
            this.loginCallback(response);
          })
          .catch((error) => {
            this.loginFailback(error, onChallengeFailed);
          });
      };
      this.afterChallengeCallback = passwordLogin.bind(this);
    }

    if (method === 'email' || method === 'phone') {
      params.identifierId = this.identifierId;
      params.code = this.codeInputManager.getCode();

      AuthApi.loginWithCode(params)
        .then((response) => {
          this.loginCallback(response);
        })
        .catch((error) => {
          this.loginFailback(error);
        });
    }
  }

  // both MEWE and OAuth
  doRegister() {
    if (this.isAuthRequestInProgress) return;
    this.isAuthRequestInProgress = true;

    let params = {
      firstName: this.fnameValue,
      lastName: this.lnameValue,
      imOver16: true,
      agreeWithTOS: true,
      publicProfile: true,
      timeZone: jstz.determine().name(),
    };

    if (this.authProvider === AuthProviders.MEWE) {
      params.validationCode = this.codeInputManager.getCode();

      // we don't send SMS to some countries, they need to set password
      if (this.smsDisabled) {
        params.password = this.passwordValue;
      }

      if (this.isEmailFlow) {
        params.tmpEmailId = this.identifierId;
      } else {
        params.tmpPhoneId = this.identifierId;
      }
    } else {
      params.idToken = this.idToken;
    }

    params = addSignupUrlParams(params, this.urlParams);

    AuthApi.registerAccount(params, this.authProvider)
      .then((data) => {
        this.registrationCallback(params, data, this.urlParams);
      })
      .catch((error) => {
        const resp = error.data;

        if (resp.attemptsLeft > 0) {
          this.codeInputManager.setError(true);
          this.focusCodeInput();
        } else if (resp.attemptsLeft === 0) {
          this.limitError = true;
        } else {
          FunctionalUtils.showDefaultErrorMessage();
        }
        // unset only for errors, otherwise keep the state until redirection
        this.isAuthRequestInProgress = false;
      });
  }

  @action
  toggleEmailOrPhone(option) {
    this.email = '';
    this.phoneNumber = '';
    this.isIdentifierSubmitted = false;
    this.isEmailFlow = option === 'email';

    if (this.isEmailFlow) {
      this.focusEmailInput();
    }
  }

  focusEmailInput() {
    later(() => {
      const el = document.getElementById('email-input');
      el?.focus();
    }, 200);
  }

  focusCodeInput() {
    later(() => {
      const el = document.getElementById('code-input-0');
      el?.focus();
    }, 200);
  }

  focusPasswordInput() {
    later(() => {
      const el = document.getElementById('password-input');
      el?.focus();
    }, 200);
  }

  focusNameInput() {
    later(() => {
      const el = document.getElementById('first-name-input');
      el?.focus();
    }, 200);
  }

  @action
  stepBack() {
    if (this.isAuthRequestInProgress|| this.isCheckingIndentity) {
      return;
    }

    switch (this.stepNumber) {
      // Reset Password step
      case 5:
        this.goToPasswordStep();
        break;

      // Password step
      case 4:
        if (this.otpPreferred) {
          // OTP was preferred, then user switched to password step. User should go back to OTP step
          this.goToCodeStep();
        } else {
          // password was preferred (or default). User should go to back Identifier step
          this.goToIdentifierStep();
        }
        break;

      // OTP step
      case 3:
        this.clearCodeInputs();
        this.limitError = false;

        // OTP step in registration flow - always go back to Name form
        if (this.isRegisterFlow) {
          this.goToNameStep();
        } else {
          if (this.otpPreferred) {
            // if OTP was preferred then previous step is Identifier step
            this.goToIdentifierStep();
          } else {
            // Password was preferred and user switched to OTP step, prev step is Password
            this.goToPasswordStep();
          }
        }
        break;

      // Name form step
      case 2:
        this.fname = '';
        this.lname = '';
        // go back to email/phone input only for MeWe auth flow
        this.stepNumber = this.authProvider === AuthProviders.MEWE ? 1 : 0;
        break;

      // Identifier form step
      case 1:
        this.goToStepZero();
        break;
    }

    this.isIdentifierSubmitted = false;
  }

  @action
  goToApp() {
    this.urlParams.next = getRedirToInnerAppQueryParam();
    this.redirectAfterLogin(this.urlParams);
  }

  @action
  resendEmail(e) {
    e?.preventDefault?.();

    AuthApi.resendConfirmationLink(this.emailValue).then(() => {
      this.emailSentText = __('A new validation email has been sent to {email}', { email: this.emailValue });
    });
  }

  @action
  toggleAgeConsent() {
    this.ageConsent = !this.ageConsent;
  }

  @action
  challengeReadyCallback() {
    this.challengeReady = true;
  }

  @action
  submitName(e) {
    e?.preventDefault?.();

    // in MEWE flow user has to pass code after entering name
    if (this.authProvider === AuthProviders.MEWE) {
      // don't send code for the second time if user navigated from OTP code back and then submit again
      if (this.otpAlreadySent) {
        if (this.cannotRegisterWithCC) {
          this.showNotSupportedCountry();
        } else {
          this.goToCodeStep();
        }
        return;
      }

      this.isAuthRequestInProgress = true;

      this.sendCode(() => {
        this.goToCodeStep();
        this.isAuthRequestInProgress = false;
      });
      return;
    }

    // in Apple/Google flow, entering name is the last step
    if (this.isRegisterFlow) {
      this.doRegister();
    } else {
      this.loginWithToken();
    }
  }

  @action
  codeKeyDown(index, event) {
    this.codeInputManager.handleKeyDown(index, event);
  }

  @action
  codeFocus(event) {
    this.codeInputManager.handleFocus(event);
  }

  @action
  codeKeyUp(index, event) {
    this.codeInputManager.handleKeyUp(index, event, () => {
      this.submitCode();
    });
    this.isCodeComplete = this.codeInputManager.isComplete();
  }

  @action
  codeInput(index, event) {
    this.codeInputManager.handleInput(index, event, () => {
      this.submitCode();
    });
    // Update completion state after input handling
    this.isCodeComplete = this.codeInputManager.isComplete();
    // Also update after a brief delay to ensure all processing is complete
    setTimeout(() => {
      this.isCodeComplete = this.codeInputManager.isComplete();
    }, 0);
  }

  @action
  codePaste(index, event) {
    this.codeInputManager.handlePaste(index, event, () => {
      this.submitCode();
    });
    // Update completion state after paste handling
    this.isCodeComplete = this.codeInputManager.isComplete();
    // Also update after a brief delay to ensure all processing is complete
    setTimeout(() => {
      this.isCodeComplete = this.codeInputManager.isComplete();
    }, 0);
  }

  @action
  submitCode(e) {
    e?.preventDefault?.();

    // If manual submission is required, trigger manual submit
    if (this.codeInputManager.requiresManualSubmission) {
      this.codeInputManager.manualSubmit(() => {
        // Call the actual submission logic
        const code = this.codeInputManager.getCode();
        if (code.length === 6) {
          if (this.isRegisterFlow) {
            this.doRegister();
          } else {
            this.loginWithMewe(this.isEmailFlow ? 'email' : 'phone');
          }
        } else {
          this.codeInputManager.setError(true);
        }
      });
      return;
    }

    const code = this.codeInputManager.getCode();

    if (code.length === 6) {

      if (this.isRegisterFlow) {
        this.doRegister();
      } else {
        this.loginWithMewe(this.isEmailFlow ? 'email' : 'phone');
      }

    } else {
      this.codeInputManager.setError(true);
    }
  }

  @action
  resetFlow() {
    window.location.reload();
  }

  @action
  clearCodeInputs() {
    this.codeInputManager.clear();
  }

  @action
  bindPasswordActionLink(element) {
    this.passwordActionLink = element.querySelector('b');
    this.passwordActionLink.addEventListener('click', this.goToPasswordResetStep);
  }

  @action
  unbindPasswordActionLink() {
    this.passwordActionLink.removeEventListener('click', this.goToPasswordResetStep);
  }

  get codeInputValues() {
    return this.codeInputManager.getValuesForTemplate();
  }

  loginFailback(error, onChallengeFailed) {
    const resp = error.data;

    // some old accounts that were not confirmed yet.
    // this check probably can be removed sometime after releasing new auth
    if (error.status === 401) {
      if (resp.block) {
        this.loginLockedUntil = timeFromNow(resp.block * 1000, Date.now(), i18n.language);
        this.loginBlocked = true;
        cookie.set('loginBlockedUntil', resp.block * 1000);
      } else {
        this.invalidPassword = true;
        this.focusPasswordInput();
      }
    } else if (error.status === 400 && resp?.errorCode == 118) {
      onChallengeFailed?.(); // can happen only for password login protected by captcha
    } else if (error.status === 409) {
      this.needConfirmEmail = true;
    } else if (resp.attemptsLeft > 0) {
      this.codeInputManager.setError(true);
      this.focusCodeInput();
    } else if (resp.attemptsLeft === 0) {
      this.limitError = true;
    } else {
      FunctionalUtils.showDefaultErrorMessage();
    }
    // unset only for errors, otherwise keep the state until redirection
    this.isAuthRequestInProgress = false;
  }

  loginCallback(loginResponse) {
    const user = loginResponse.user;

    tokenManager.set(loginResponse, false);
    CurrentUserStore.send('handle', user, true);

    CurrentUserStore.getState().deferred.promise.then(() => {
      if (isInJail(loginResponse)) {
        this.router.transitionTo('lockout');
      } else if (isLocked(loginResponse)) {
        this.router.transitionTo('verify');
      } else {
        this.redirectAfterLogin();
      }
    });
  }

  redirectAfterLogin() {
    const urlParams = this.urlParams || {};

    if (urlParams.next) {
      // use substring of href if possible instead of urlParams.next to avoid loosing query params from next url
      const nextFromUrl = getNextFromUrl();

      if (nextFromUrl) {
        window.location.href = nextFromUrl;
      } else {
        window.location.href = urlParams.next;
      }

      if (urlParams.next.indexOf('?download') !== -1) {
        window.setTimeout(() => (window.location.href = '/myworld'), 3500);
      }
    } else {
      this.doRedirect(urlParams, true);
    }
  }

  registrationCallback(registrationParams, registrationResponse, urlParams) {
    const params = registrationParams;
    const user = registrationResponse.user;

    if (!user.locale && params.locale) {
      user.locale = params.locale;
    }

    tokenManager.set({ user: user });

    urlParams.hash = '#new-user';

    // redirection is delayed to make sure that token manager finished its job
    later(this, () => this.doRedirect(urlParams), 1000);
  }

  doRedirect(urlParams, afterLogin = false) {
    const hash = urlParams?.hash || '';

    // case for registration by email
    let cmpgn = urlParams?.cmpgn || '';

    // case for registration by sms
    let urlObj = new URL(window.location.href);
    // in future we can change it to more generic campaign parameter
    if (urlObj.searchParams.has('brave')) {
      cmpgn = 'brave';
    }

    if (urlParams) {
      // profile/page redirection - url is simply `/{publicId}` in both cases
      if (urlParams.ppId || urlParams.upId) {
        return (window.location = `/${urlParams.ppId || urlParams.upId}${hash}`);
      }

      if (urlParams.epId) {
        // event redirection - if there is `id-` prefix indicating actual eventId then
        // we go straight to event, otherwise it's publicId and we use it to fetch event public data to get eventId.
        const nonPublicId = ~urlParams.epId.indexOf('id-') ? urlParams.epId.replace('id-', '') : null;

        if (nonPublicId) {
          return (window.location = `/event/${nonPublicId}${hash}`);
        } else {
          return PublicPagesApi.getEvent(urlParams.epId).then(
            (data) => (window.location = `/event/${data.event.id}${hash}`)
          );
        }
      }

      if (urlParams.gpId) {
        // group redirection - if there is `id-` prefix indicating actual groupId then we go straight to group,
        // otherwise it's publicId and we use it to fetch group public data to get groupId.
        // - usecase is when we already checked on public page that user can go straigh to group, no need to repeat that.
        // - other usecase is private group invitation where we don't have publicId in the process
        const nonPublicId = ~urlParams.gpId.indexOf('id-') ? urlParams.gpId.replace('id-', '') : null;

        if (nonPublicId) {
          return (window.location = `/group/${nonPublicId}${hash}`);
        } else {
          return PublicPagesApi.group(urlParams.gpId).then((data) => {
            if (data.isMember || data.isInvited) {
              return (window.location = `/group/${data.id}${hash}`);
            } else {
              return (window.location = `/myworld?previewGroupId=${data.id}${hash}`);
            }
          });
        }
      }
    }

    // Campaign happens only with no other params (no group/page/event/user invitation)
    if (cmpgn) {
      return (window.location = `/myworld?${cmpgn}${hash}`);
    }

    if (afterLogin) {
      window.location = `/myworld${hash}`;
     } else {

      // if there was no specific redirection to group/profile etc.
      // we can start FTUE right away for better UX without redirecting first
      // -------
      // but firstly get current user because it's needed in FTUE process for the
      // Premium payment where we e.g. prefill user name / email
      AccountApi.getCurrentUser()
        .then((data) => {
          CurrentUserStore.send('handle', data, true);

          startFtue(this.dynamicDialogs, {
            onClose: () => {
              window.location = `/myworld${hash}`;
            }
          });
        })
        .catch(() => {
          // simple redirection just in case
          window.location = `/myworld${hash}`;
        });
    }
  }

  showNotSupportedCountry() {
    this.dynamicDialogs.openDialog('simple-dialog-new', {
      title: __('Sorry'),
      message: __('SMS registration is currently not available in your region.'),
      cancelButtonText: __('Close'),
      okButtonText: __('Use email'),
      onConfirm: () => {
        this.toggleEmailOrPhone('email');
        this.goToIdentifierStep();
      },
      onCancel: () => {
        this.goToStepZero();
      },
    });
  }
}
